import { fromJS } from 'immutable';
import { SubmissionError } from 'redux-form/immutable';
import { createActions } from 'redux-actions';
import { Auth } from 'aws-amplify';

import * as t from './actionTypes';

const actionCreators = createActions({
  [t.NETWORK_START]: undefined,
  [t.NETWORK_RESET]: undefined,
  [t.NETWORK_ERROR]: undefined,
  [t.USER_LOGIN]: undefined,
  [t.USER_LOGOUT]: undefined,
  [t.IS_AUTHENTICATED_SET]: undefined,
  [t.IS_AUTHENTICATING_SET]: undefined
});

/**
 * Attempt to authenticate user – note session will subsequently be instated in loadSession method
 * @param  {string} email    The user's email address
 * @param  {string} password The user's password
 * @param  {object} history  react-router-dom history object
 * @throws {SubmissionError}
 * @return {[type]}          [description]
 */
export const login = (email, password, history) => async dispatch => {
  dispatch(actionCreators.auth.networkStart());
  try {
    await Auth.signIn(email, password);
  } catch (e) {
    dispatch(actionCreators.auth.networkReset());
    if (e.code === 'UserNotConfirmedException') {
      await resendSignUpConfirmation(email);
      throw new SubmissionError({
        _error:
          'You must confirm your account before you can log in – a new confirmation link has been sent by email.'
      });
    }
    throw new SubmissionError({ email: e.message });
  }

  // Login successful
  dispatch(actionCreators.auth.networkReset());
  history.push('/sessions');
};

/**
 * Resend the signup email with confirmation link
 * @param  {string} email The email address of the user to resend the confirmation link to
 * @throws {SubmissionError}
 * @return {[type]}       [description]
 */
const resendSignUpConfirmation = async email => {
  try {
    await Auth.resendSignUp(email);
  } catch (resendError) {
    throw new SubmissionError({
      _error:
        "Account not confirmed but couldn't resend link: " + resendError.message
    });
  }
};

/**
 * Reinstate user's session, or redirect to login if no authenticated session exists
 * @param  {[type]} history [description]
 * @return {[type]}         [description]
 */
export const loadSession = history => async dispatch => {
  dispatch(actionCreators.auth.networkStart());
  let user;
  try {
    if (await Auth.currentSession()) {
      user = await Auth.currentAuthenticatedUser();
    }
  } catch (e) {
    let errorMessage;
    if (e.code === 'NetworkError') {
      errorMessage = 'Could not connect to network.';
    } else if (e === 'No current user') {
      errorMessage = 'Please log in again.';
    } else {
      errorMessage = e;
    }

    // Not logged in
    dispatch(actionCreators.auth.networkError(errorMessage));
    dispatch(actionCreators.auth.isAuthenticatingSet(false));
    dispatch(actionCreators.auth.isAuthenticatedSet(false));

    // Redirect to login
    history.push('/auth/login');
    return;
  }

  // Session reinstated
  dispatch(actionCreators.auth.networkReset());
  dispatch(actionCreators.auth.isAuthenticatingSet(false));
  dispatch(actionCreators.auth.isAuthenticatedSet(true));
  dispatch(
    actionCreators.auth.userLogin(
      fromJS({
        ...user.attributes,
        id: user.username
      })
    )
  );
};

/**
 * Log user out
 * @param  {object} history  react-router-dom history object
 * @return {[type]}         [description]
 */
export const logout = history => async dispatch => {
  dispatch(actionCreators.auth.networkStart());
  await Auth.signOut();
  dispatch(actionCreators.auth.userLogout());
  dispatch(actionCreators.auth.networkReset());
  history.push('/auth/login', {
    message: 'You have been logged out.',
    messageClassSuffix: 'success'
  });
};

/**
 * Attempt new user signup
 * @param  {[type]} values [description]
 * @throws {SubmissionError}
 * @return {[type]}        [description]
 */
export const signup = values => async dispatch => {
  dispatch(actionCreators.auth.networkStart());
  try {
    await Auth.signUp({
      username: values.get('email'),
      password: values.get('password'),
      attributes: {
        given_name: values.get('firstName'),
        family_name: values.get('lastName')
      }
    });
  } catch (e) {
    dispatch(actionCreators.auth.networkError());
    if (e.code === 'UsernameExistsException') {
      throw new SubmissionError({
        _error: 'Account already exists, please log in.'
      });
    }
    throw new SubmissionError({ _error: e.message });
  }

  dispatch(actionCreators.auth.networkReset());
};

/**
 * Confirm signup using confirmation code sent to a new user's email address
 * @param  {string} userId [description]
 * @param  {string} code   [description]
 * @param  {object} history  react-router-dom history object
 * @return {[type]}        [description]
 */
export const signupConfirm = (userId, code, history) => async dispatch => {
  try {
    await Auth.confirmSignUp(userId, code);
  } catch (e) {
    // Confirmation failed
    let message = e.message;
    if (e.code === 'CodeMismatchException') {
      message =
        "Invalid confirmation link. Please check your email to be sure you haven't been sent a subsequent link.";
    }
    history.push('/auth/login', {
      message,
      messageClassSuffix: 'danger'
    });
    return;
  }

  // Confirmation successful, redirect to login
  history.push('/auth/login', {
    message: 'Your account has been confirmed, please log in below.',
    messageClassSuffix: 'success'
  });
};

/**
 * Initiate forgotten password process
 * @param  {string} email The email address for which the password has been forgotten
 * @throws {SubmissionError}
 * @return {[type]}        [description]
 */
export const forgotPassword = email => async dispatch => {
  dispatch(actionCreators.auth.networkStart());
  try {
    await Auth.forgotPassword(email);
  } catch (e) {
    let message = e.message;
    if (e.code === 'UserNotFoundException') {
      message = 'No active user with that email address, please try again.';
    } else if (e.code === 'InvalidParameterException') {
      resendSignUpConfirmation(email);
      message =
        'You must confirm your account before you can reset your password – a new confirmation link has been sent by email.';
    }
    dispatch(actionCreators.auth.networkError());
    throw new SubmissionError({ _error: message });
  }

  dispatch(actionCreators.auth.networkReset());
};

/**
 * Reset password using confirmation code
 * @param {string} userId The user's ID
 * @param {string} code The password reset code
 * @param {string} password The user's new password
 * @param {object} history react-router-dom history object
 */
export const resetPassword = (
  userId,
  code,
  password,
  history
) => async dispatch => {
  dispatch(actionCreators.auth.networkStart());
  try {
    await Auth.forgotPasswordSubmit(userId, code, password);
  } catch (e) {
    dispatch(actionCreators.auth.networkError());
    throw new SubmissionError({ _error: e.message });
  }

  // Success, redirect so user can log in
  dispatch(actionCreators.auth.networkReset());
  history.push('/auth/login', {
    message: 'Your password has been reset.',
    messageClassSuffix: 'success'
  });
};

export const setIsAuthenticated = isAuthenticated => async dispatch => {
  dispatch(actionCreators.auth.isAuthenticatedSet(isAuthenticated));
};

export const setIsAuthenticating = isAuthenticating => async dispatch => {
  dispatch(actionCreators.auth.isAuthenticatingSet(isAuthenticating));
};

export const setNetworkError = errorMessage => async dispatch => {
  dispatch(actionCreators.auth.networkError(errorMessage));
};



// WEBPACK FOOTER //
// ./src/modules/auth/actions.js